<script lang="ts">
  // (c) Cognisoft Aps, 2022
  // spell noun from picture
  // without model, but help after error
  // on-screen keyboard

  import { onDestroy } from "svelte";

  import { Pages, DaDK, Path, Extension, State } from "../enums";
  import { HelpLevel } from "../classes";
  import {
    afterCorrectWaitSecs,
    alfaKeyCaps,
    qwertyKeyCaps,
  } from "../constants";

  import { Icons } from "../utilities/icons";
  import { playSoundWithAudioTag } from "../utilities/playSound";
  import { playCorrectRandomWithAudioTag } from "../utilities/playCorrectRandom";
  import { getTotalNumberOfTasks } from "../utilities/getTotalNumberOfTasks";
  import { setCompletedBackgroundColor } from "../utilities/setCompletedBackgroundColor";
  import { logger } from "../utilities/logger";

  import { studentUserData } from "../stores/user-store.svelte";
  import { route } from "../stores/route.svelte";
  import {
    currentSentenceNumberForSteps,
    targetsForSteps,
  } from "../stores/targets-step567.svelte"; // last picture shown in step 4
  import { progress } from "../stores/progress.svelte";

  import { targetVerbAndObjects } from "../globals/targets";

  import { helpCount } from "../stores/helpCount.svelte";
  import { result } from "../stores/result.svelte";
  import { tasksCompleted } from "../stores/tasksCompleted.svelte";

  import ExitModal from "../modals/ExitModal.svelte";

  import RoundButton from "../components/RoundButton.svelte";
  import ProgressBar from "../components/ProgressBar.svelte";
  import PauseMessage from "../components/PauseMessage.svelte";

  // PROPS --------------------------------------------------

  // one audiotag in App.svelte is used on all pages in order to have
  //   sound played even before mouse click

  let { audioTag }: { audioTag: HTMLAudioElement } = $props();

  // CONSTANTS ---------------------------------------------

  const vowels = ["a", "e", "i", "o", "u", "y", "æ", "ø", "å"];
  // should we exlude seldom used consonants (w, q, x, z)?
  const consonants = [
    "b",
    "c",
    "d",
    "f",
    "g",
    "h",
    "j",
    "k",
    "l",
    "m",
    "n",
    "p",
    "q",
    "r",
    "s",
    "t",
    "v",
    "w",
    "x",
    "z",
  ];

  const showModelTimeSecs: number = 3;
  const numberOfHelpLevels = 6;

  // STATE ------------------------------------------------

  let bipAudioTag: HTMLAudioElement;

  let keyCaps: Array<string> = $state([]); // on-screen keyboard keys, qwerty or alfa

  let currentState: State = $state(State.PresentTask); // currentState for task solution for each item/picture
  let answerWord = $state("");
  let model = $state("");

  let repeatButtonTimer: ReturnType<typeof setTimeout>;
  let helpTimer: ReturnType<typeof setTimeout>;

  // help both after a set time, requested and after error
  let helpLevel = $state(new HelpLevel(numberOfHelpLevels));

  let allowRepetition = $state(false);
  let requestedHelpPaused = $state(false); // // help not active for some seconds after activted last time

  let helpLetters: Array<string> = $state([]);

  // for results:
  let gotHelpForItem = $state(false);
  let hasErrorForItem = $state(false);

  // FOR RESEARCH ONLY
  // // save results for each response, push to array in results.store
  // let presentTime: Date;
  // let numberOfTimedHelp = 0;
  // let numberOfRequestedHelp = 0;
  // let numberOfRequestedRepetitions = 0;
  // let attempt = 1;
  // let responseDetails: Array<ResponseDetail> = [];

  // keep track of pause time
  let pauseStartTime: number;
  let pauseEndTime: number;

  // EFFECTS --------------------------------------------------

  $effect(() => {
    logger("Step7 currentState:", currentState);
  });
  $effect(() => {
    logger("Step7 helpLevel:", helpLevel.level);
  });

  // FUNCTIONS --------------------------------------------

  const playErrorSound = () => {
    bipAudioTag.src = Path.Snd + "bip" + Extension.Mp3;
    bipAudioTag.load();
    bipAudioTag.play();
  };

  const countHelp = () => {
    switch (currentSentenceNumberForSteps.value) {
      case 0:
        helpCount.value.spelling.item71++;
        break;
      case 1:
        helpCount.value.spelling.item72++;
        break;
      case 2:
        helpCount.value.spelling.item73++;
        break;
      case 3:
        helpCount.value.spelling.item74++;
        break;
    }
  };

  // adjust help count by number of letters in word to get a mean
  const adjustHelpCount = () => {
    switch (currentSentenceNumberForSteps.value) {
      case 0:
        helpCount.value.spelling.item71 =
          helpCount.value.spelling.item71 /
          targetVerbAndObjects.value[
            targetsForSteps.value[currentSentenceNumberForSteps.value]
          ].object.length;
        break;
      case 1:
        helpCount.value.spelling.item72 =
          helpCount.value.spelling.item72 /
          targetVerbAndObjects.value[
            targetsForSteps.value[currentSentenceNumberForSteps.value]
          ].object.length;
        break;
      case 2:
        helpCount.value.spelling.item73 =
          helpCount.value.spelling.item73 /
          targetVerbAndObjects.value[
            targetsForSteps.value[currentSentenceNumberForSteps.value]
          ].object.length;
        break;
      case 3:
        helpCount.value.spelling.item74 =
          helpCount.value.spelling.item74 /
          targetVerbAndObjects.value[
            targetsForSteps.value[currentSentenceNumberForSteps.value]
          ].object.length;
        break;
    }
  };

  // show repeat button a defined time after showin task

  const startRepeatButtonTimer = () => {
    if (currentState === State.WaitAfterEnd) return;
    allowRepetition = false;
    repeatButtonTimer = setTimeout(() => {
      allowRepetition = true;
    }, studentUserData.value.settings.repeatDelaySecs * 1000);
  };

  // showing help according to 2 level hierarkies (timed and requested help) ---

  const startHideModelTimer = () => {
    if (currentState === State.WaitAfterEnd) return;
    setTimeout(() => {
      requestedHelpPaused = false;
      model = "";
    }, showModelTimeSecs * 1000);
  };

  const enableHelpButtonDelayed = () => {
    setTimeout(() => {
      requestedHelpPaused = false;
    }, 1000);
  };

  const startHelpTimer = () => {
    if (currentState === State.WaitAfterEnd) return;
    currentState = State.WaitForResponse;
    requestedHelpPaused = false;
    helpTimer = setTimeout(() => {
      giveHelp(false);
    }, studentUserData.value.settings.helpDelaySecs * 1000);
  };

  const startHelpTimerAndWaitForResponse = (afterError: boolean) => {
    if (currentState === State.WaitAfterEnd) return;
    currentState = State.WaitForResponse;
    startHelpTimer();
    // FOR RESEARCH ONLY:
    // if (afterError) {
    //   initializeResultVariables();
    // }
  };

  // showing help according to evel hierarky

  const make3helpLetters = (
    targetLetter: string,
    letters: Array<string>,
  ): Array<string> => {
    let _helpLetters: Array<string> = [];
    _helpLetters.push(targetLetter);
    let secondInserted: string;
    do {
      secondInserted = letters[Math.floor(Math.random() * letters.length)];
    } while (secondInserted === targetLetter);
    _helpLetters.push(secondInserted);
    let thirdInserted: string;
    do {
      thirdInserted = letters[Math.floor(Math.random() * letters.length)];
    } while (thirdInserted === targetLetter || thirdInserted == secondInserted);
    _helpLetters.push(thirdInserted);
    return _helpLetters;
  };

  const make3HelpVowels = (targetVowel: string): Array<string> => {
    return make3helpLetters(targetVowel, vowels);
  };

  const make3HelpConsonants = (targetConsonant: string): Array<string> => {
    return make3helpLetters(targetConsonant, consonants);
  };

  const repeatObjectHelp = (afterError: boolean) => {
    helpLevel.setNextLevel();
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object,
      startHelpTimerAndWaitForResponse.bind(null, afterError),
    );
  };

  const show3LettersHelp = (afterError: boolean) => {
    helpLevel.setNextLevel();
    const letterPosition = answerWord.length;
    const targetLetter =
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object.charAt(letterPosition);
    if (vowels.includes(targetLetter)) {
      helpLetters = [...make3HelpVowels(targetLetter)];
    } else {
      helpLetters = [...make3HelpConsonants(targetLetter)];
    }
    startHelpTimerAndWaitForResponse(afterError);
  };

  const showModelWordHelp = (afterError: boolean) => {
    helpLetters = [];
    helpLevel.setNextLevel();
    model =
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object;
    startHideModelTimer();
    startHelpTimerAndWaitForResponse(afterError);
  };

  const showCorrectLetterHelp = (afterError: boolean) => {
    const letterPosition = answerWord.length;
    helpLevel.setNextLevel();
    helpLetters.push(
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object.charAt(letterPosition),
    );
    helpLetters = helpLetters;
    enableHelpButtonDelayed();
    startHelpTimerAndWaitForResponse(afterError);
  };

  const insertCorrectLetterHelp = (afterError: boolean) => {
    const letterPosition = answerWord.length - 1;
    const insertLetter = targetVerbAndObjects.value[
      targetsForSteps.value[currentSentenceNumberForSteps.value]
    ].object.charAt(letterPosition + 1);
    playSoundWithAudioTag(audioTag, insertLetter, null);
    answerWord = answerWord + insertLetter;
    helpLetters = [];
    if (
      answerWord ===
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object
    ) {
      currentState = State.Correct;
      clearTimeout(helpTimer);
      // playCorrectRandomWithAudioTag(audioTag, afterCorrect);
      afterCorrect();
    } else {
      helpLevel.setNextLevel();
      currentState = State.WaitForResponse;
      enableHelpButtonDelayed();
      clearTimeout(helpTimer);
      startHelpTimerAndWaitForResponse(afterError);
    }
  };

  // give requested help after clicking help button or after timed help according to help level
  const giveHelp = (afterError: boolean) => {
    if (
      currentState !== State.WaitForResponse &&
      currentState !== State.Error
    ) {
      return;
    }
    currentState = State.GivingHelp;
    logger("Step7 giveHelp, helpLevel:", helpLevel.level);
    countHelp();
    gotHelpForItem = true;
    requestedHelpPaused = true;
    clearTimeout(helpTimer);
    switch (helpLevel.level) {
      case 1:
      case 2:
        repeatObjectHelp(afterError);
        break;
      case 3:
        show3LettersHelp(afterError);
        break;
      case 4:
        showModelWordHelp(afterError);
        break;
      case 5:
        showCorrectLetterHelp(afterError);
        break;
      case 6:
        insertCorrectLetterHelp(afterError);
        break;
      default:
        console.error(
          "step 7, error in giveHelp() switch, helpLevel: ",
          helpLevel.level,
        );
    }
  };

  const requestedHelp = (): void => {
    logger("Step7 requestedHelp");
    if (currentState !== State.WaitForResponse) {
      return;
    }
    currentState = State.GivingHelp;
    countHelp();
    // numberOfRequestedHelp++; // FOR RESEARCH ONLY
    requestedHelpPaused = true;
    clearTimeout(helpTimer);
    playSoundWithAudioTag(audioTag, DaDK.Step7Help, startHelpTimer);
  };

  const repeatSound = () => {
    if (currentState !== State.WaitForResponse) {
      return;
    }
    currentState = State.RepeatingSound;
    countHelp();
    // numberOfRequestedRepetitions++;  // FOR RESEARCH ONLY
    clearTimeout(helpTimer);
    requestedHelpPaused = true;
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object,
      startHelpTimer,
    );
  };

  // start ---

  const startAndSayIntro = (): void => {
    playSoundWithAudioTag(audioTag, DaDK.Step7Intro, startAndSayWord);
  };

  const startAndSayWord = () => {
    playSoundWithAudioTag(audioTag, DaDK.Write, startAndSayWord2);
  };

  const startAndSayWord2 = (): void => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object,
      WaitForResponse,
    );
  };

  const WaitForResponse = (): void => {
    currentState = State.WaitForResponse;
    startRepeatButtonTimer();
    startHelpTimer();
    // initializeResultVariables(); // FOR RESEARCH ONLY
  };

  // correct ---
  const afterCorrect = (): void => {
    setCompletedBackgroundColor(true);
    tasksCompleted.value = tasksCompleted.value + 1;
    // result.value.responseDetails.push(...responseDetails);
    result.value.lastStepCompleted = 7;
    if (!gotHelpForItem && !hasErrorForItem) {
      result.value.spellingCorrectFirstTry =
        result.value.spellingCorrectFirstTry + 1;
    }
    adjustHelpCount();
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object,
      null,
    );
    currentState = State.WaitAfterEnd;
    setTimeout(() => {
      if (currentSentenceNumberForSteps.value < 3) {
        currentSentenceNumberForSteps.value =
          currentSentenceNumberForSteps.value + 1;
        route.current = Pages.Step5;
      } else {
        route.current = Pages.Step8;
      }
    }, afterCorrectWaitSecs);
  };

  // button click event handling ---

  const keyClicked = (letter: string): void => {
    const letterPosition = answerWord.length;
    if (
      letter ===
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object.charAt(letterPosition)
    ) {
      // saveResultVariablesWithCorrect(true);  // FOR RESEARCH ONLY
      helpLetters = [];
      answerWord = answerWord + letter;
      helpLevel.reset();
      clearTimeout(helpTimer);
      clearTimeout(repeatButtonTimer);
      if (
        answerWord ===
        targetVerbAndObjects.value[
          targetsForSteps.value[currentSentenceNumberForSteps.value]
        ].object
      ) {
        // attempt = 1; // FOR RESEARCH ONLY
        currentState = State.Correct;
        progress.value += 1;
        progress;
        allowRepetition = false;
        playCorrectRandomWithAudioTag(audioTag, afterCorrect);
      } else {
        WaitForResponse();
      }
    } else {
      // saveResultVariablesWithCorrect(false);  // FOR RESEARCH ONLY
      // attempt++; // FOR RESEARCH ONLY
      currentState = State.Error;
      hasErrorForItem = true;
      if (helpLevel.level === numberOfHelpLevels) {
        giveHelp(true);
      } else {
        playSoundWithAudioTag(
          audioTag,
          DaDK.TryAgain,
          giveHelp.bind(null, true),
        );
      }
    }
  };

  // keyboard input ---

  const handleKeydown = (event: KeyboardEvent): void => {
    if (currentState !== State.WaitForResponse) {
      playErrorSound();
      return;
    }
    let letter = event.key.toLocaleLowerCase();
    if (keyCaps.includes(letter)) {
      keyClicked(letter);
    } else if (event.key === "?" && !requestedHelpPaused) {
      requestedHelp();
    }
  };

  // pause --------

  const pauseClicked = (): void => {
    if (currentState === State.Paused) {
      pauseEndTime = Date.now();
      result.value.pauseElapsed =
        result.value.pauseElapsed + Math.abs(pauseEndTime - pauseStartTime);
      allowRepetition = true;
      startHelpTimer();
    } else {
      pauseStartTime = Date.now();
      clearTimeout(repeatButtonTimer);
      clearTimeout(helpTimer);
      currentState = State.Paused;
    }
  };

  // exit task prematurely after stop button clicked

  // stop button: show yes-no modal
  const exitButtonClicked = () => {
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    currentState = State.ExitModalShown;
  };

  // exit task (modal event "yes")
  const exitTask = () => {
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    result.value.wasInterrupted = true;
    route.current = Pages.EndResults;
  };

  // dismiss exit modal (event "no")
  const dismissExitModal = () => {
    allowRepetition = true;
    startHelpTimer();
  };

  // FOR RESEARCH ONLY --------------------------------------------
  // save result details for a single item, used for cvs results
  // *** used for research disabled in current production
  // const saveResultVariablesWithCorrect = (correct: boolean) => {
  // // for research - disabled in current production
  // responseDetails.push({
  //   step: 7,
  //   attempt: attempt,
  //   presentTime: presentTime,
  //   responseTime: new Date(),
  //   targetItem:
  //     targetVerbAndObjects.value[targetsForSteps.value[currentSentenceNumberForSteps.value]]
  //       .object,
  //   correct: correct,
  //   numberOfTimedHelp: numberOfTimedHelp,
  //   numberOfRequestedHelp: numberOfRequestedHelp,
  //   numberOfRequestedRepetitions: numberOfRequestedRepetitions,
  // });
  // };

  // LIFE CYCLE ---------------------------------------------------

  onDestroy(() => {
    clearTimeout(helpTimer);
    clearTimeout(repeatButtonTimer);
  });

  // INITIALIZE --------------------------------------------

  // FOR RESEARCH ONLY
  // const initializeResultVariables = () => {
  //   presentTime = new Date();
  //   numberOfTimedHelp = 0;
  //   numberOfRequestedHelp = 0;
  //   numberOfRequestedRepetitions = 0;
  // };

  setCompletedBackgroundColor(false);
  if (studentUserData.value.settings.qwertyKeyboard) {
    keyCaps = [...qwertyKeyCaps];
  } else {
    keyCaps = [...alfaKeyCaps];
  }

  startAndSayIntro();
</script>

<!-- HTML ===================================================== -->

<svelte:window onkeydown={handleKeydown} />

<audio bind:this={bipAudioTag}></audio>

<main data-page="step7">
  <!-- data for Playwright tests -->
  <div
    data-testid="state"
    data-state={currentState}
    style="display: none;"
  ></div>
  <div
    data-testid="word"
    data-word={targetVerbAndObjects.value[
      targetsForSteps.value[currentSentenceNumberForSteps.value]
    ].object}
    style="display: none;"
  ></div>
  <!-- top panel with help and repeat buttons -->
  <div class="top-panel">
    <div>
      <span class="stop-button">
        <RoundButton
          disabled={currentState !== State.WaitForResponse}
          onclick={exitButtonClicked}
          icon={Icons.TrafficLightRed}
        />
      </span>
      <RoundButton
        disabled={currentState !== State.WaitForResponse &&
          currentState !== State.Paused}
        onclick={pauseClicked}
        icon={currentState === State.Paused ? Icons.Play : Icons.Pause}
      />
    </div>
    <div>
      <RoundButton
        disabled={currentState !== State.WaitForResponse || !allowRepetition}
        onclick={repeatSound}
        icon={Icons.VolumeRegular}
      />
      <span class="help-button">
        <RoundButton
          disabled={currentState !== State.WaitForResponse ||
            requestedHelpPaused}
          onclick={requestedHelp}
          icon={Icons.QuestionSolid}
        />
      </span>
    </div>
  </div>
  <!-- image showing target -->
  <div
    class="image"
    style="background-image: url('{encodeURI(
      Path.ImgBg +
        targetVerbAndObjects.value[
          targetsForSteps.value[currentSentenceNumberForSteps.value]
        ].image +
        Extension.ImgBg,
    )}');
       background-image: image-set('{encodeURI(
      Path.ImgBg +
        targetVerbAndObjects.value[
          targetsForSteps.value[currentSentenceNumberForSteps.value]
        ].image +
        Extension.WebpBg,
    )}'); 
      background-size: cover;"
  >
    <picture>
      <source
        type="image/webp"
        srcset={encodeURI(
          Path.Img +
            targetVerbAndObjects.value[
              targetsForSteps.value[currentSentenceNumberForSteps.value]
            ].image +
            Extension.Webp,
        )}
        draggable={false}
      />
      <img
        draggable={false}
        src={encodeURI(
          Path.Img +
            targetVerbAndObjects.value[
              targetsForSteps.value[currentSentenceNumberForSteps.value]
            ].image +
            Extension.Png,
        )}
        alt=""
      />
    </picture>
  </div>
  <!-- word model to copy from without stressed vowel -->
  <div class="model-div">
    {model}
  </div>
  <!-- copied word -->
  <div class="copy-div">
    {answerWord}
  </div>

  <!-- on-screen keyboard -->
  <div
    class="keyboard-div"
    class:qwerty-keys={studentUserData.value.settings.qwertyKeyboard}
  >
    {#each keyCaps as keyCap}
      <button
        class="key-cap key-cap-{keyCap}"
        class:key-help={helpLetters.includes(keyCap) &&
          currentState === State.WaitForResponse}
        disabled={currentState !== State.WaitForResponse}
        onclick={() => keyClicked(keyCap)}
      >
        {keyCap}
      </button>
    {/each}
  </div>
  <div class="progress">
    <ProgressBar
      max={getTotalNumberOfTasks(studentUserData.value.currentLevel)}
      position={progress.value}
    />
  </div>

  <!-- MODALS -------------------------------------- -->
  {#if currentState === State.Paused}
    <PauseMessage pauseMessageClicked={pauseClicked} />
  {/if}

  {#if currentState === State.ExitModalShown}
    <ExitModal yes={exitTask} no={dismissExitModal} />
  {/if}
</main>

<!-- CSS ====================================================== -->
<style>
  main {
    margin-left: auto;
    margin-right: auto;
    width: 120vh;
    height: calc(var(--vh, 1vh) * 100);
    display: grid;
    grid-template-columns: 100%;
    grid-template-rows: 14% 30% 12% 10% 31% 2%;
    grid-template-areas:
      "top"
      "img"
      "word-model"
      "word-copy"
      "keyboard"
      "prog";
    gap: 1%;
    padding: 3%;
    padding-top: 2%;
    padding-left: 2%;
    padding-right: 1.5%;
  }
  @media screen and (max-aspect-ratio: 6/5) {
    main {
      width: 100vw;
    }
  }
  .top-panel {
    grid-area: top;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .image {
    background: white;
    border: solid 1px black;
    grid-area: img;
    width: 35%;
  }
  @media screen and (max-aspect-ratio: 5/5) {
    .image {
      width: 50%;
    }
  }
  @media screen and (max-aspect-ratio: 4/5) {
    .image {
      width: 60%;
    }
  }
  @media screen and (max-aspect-ratio: 3.5/5) {
    .image {
      width: 70%;
    }
  }
  @media screen and (max-aspect-ratio: 3/5) {
    .image {
      width: 80%;
    }
  }
  @media screen and (max-aspect-ratio: 2/5) {
    .image {
      width: 90%;
    }
  }
  @media screen and (max-aspect-ratio: 1/5) {
    .image {
      width: 100%;
    }
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: bottom;
  }
  .model-div {
    grid-area: word-model;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-size: 8vmin;
    text-transform: uppercase;
    letter-spacing: 2vmin;
    user-select: none;
  }
  .progress {
    grid-area: prog;
    width: 100%;
  }
  .copy-div {
    grid-area: word-copy;
    background: white;
    border: solid 1px black;
    font-size: 7.5vmin;
    text-transform: uppercase;
    letter-spacing: 2vmin;
    padding-left: 2vmin;
    margin-bottom: 1vmin;
    line-height: 1.05;
    user-select: none;
  }
  .keyboard-div {
    grid-area: keyboard;
    display: grid;
    width: 100%;
    grid-template-columns: repeat(10, 1fr);
    grid-template-rows: repeat(3, 1fr);
  }
  .qwerty-keys {
    grid-template-columns: repeat(11, 1fr);
  }
  .key-cap {
    width: 94%;
    height: calc(var(--vh, 1vh) * 9);
    font-size: 7vmin;
    padding: 0;
    text-transform: uppercase;
    color: black;
    border: solid 0.45vmin var(--button-border-color);
    border-radius: 1vmin;
    background: white;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    user-select: none;
  }
  .key-cap[disabled] {
    color: gray;
    border-color: gray;
    cursor: auto;
  }
  @media (hover) {
    .key-cap:not([disabled]):hover {
      background-color: var(--key-hover-background-color);
      border-color: var(--key-hover-border-color);
    }
  }
  .key-help {
    background: var(--key-help-background-color);
  }
</style>
